import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

const TIER_INFO = {
  homeowner_seasonal: {
    name: 'Homeowner Seasonal',
    price: '$50',
    period: '6 months',
    color: 'green',
    badge: 'POPULAR',
    features: [
      'GDD Tracking (unlimited properties)',
      'PGR & Weed Control Departments',
      'Equipment Log & Spreader Settings',
      'Seasonal Calendar & Email Alerts',
      'Zone-based property management'
    ]
  },
  enthusiast_annual: {
    name: 'Enthusiast Annual',
    price: '$80',
    period: 'year',
    color: 'blue',
    badge: 'BEST VALUE',
    features: [
      'Everything in Seasonal, PLUS:',
      'Pest & Disease Departments',
      'Soil Analysis & Recommendations',
      '5-Day Spray Forecast',
      'Unlimited zones & Priority support'
    ]
  }
};

// Map features to required tier
const FEATURE_REQUIRED_TIER = {
  weed_department: 'homeowner_seasonal',
  equipment_log: 'homeowner_seasonal',
  spreader_settings: 'homeowner_seasonal',
  email_alerts: 'homeowner_seasonal',
  zone_management: 'homeowner_seasonal',
  field_guide: 'homeowner_seasonal',
  pest_department: 'enthusiast_annual',
  disease_department: 'enthusiast_annual',
  soil_analysis: 'enthusiast_annual',
  spray_forecast: 'enthusiast_annual',
  unlimited_zones: 'enthusiast_annual',
  priority_support: 'enthusiast_annual',
};

// Outcome-framed copy per feature — what the user GETS, not what's locked.
const FEATURE_OUTCOMES = {
  weed_department: {
    headline: 'Stop crabgrass before it starts.',
    body: 'Track pre-emergent timing by GDD — hit the window every spring so your lawn doesn\'t get overrun.',
    bullets: [
      'GDD-based crabgrass & Poa timing',
      'Product library with dialed-in rates',
      'Application log that warns on overlap',
    ],
    cta: 'Get Weed Control Timing',
  },
  equipment_log: {
    headline: 'Never guess when your mower was last serviced.',
    body: 'Log oil changes, blade sharpens, and belt swaps. Get a reminder before the season starts — not mid-stripe.',
    bullets: [
      'Service history per mower, trimmer, spreader',
      'Total cost tracking per machine',
      'Next-service reminders',
    ],
    cta: 'Start My Equipment Log',
  },
  spreader_settings: {
    headline: 'Dial in your spreader on the first pass.',
    body: 'Community-verified settings for your exact spreader and product — so you apply the right rate without calibrating in the driveway.',
    bullets: [
      'Settings for every popular spreader',
      'Fertilizer, pre-em, seed, lime',
      'Submit & vote your own settings',
    ],
    cta: 'Unlock Spreader Library',
  },
  email_alerts: {
    headline: 'Know the moment your lawn needs you.',
    body: 'Get pinged when GDD hits the pre-emergent window, when armyworms are migrating, or when spray conditions turn ideal.',
    bullets: [
      'GDD threshold alerts',
      'Pest & disease warnings',
      'Best-day-to-spray notifications',
    ],
    cta: 'Turn On Lawn Alerts',
  },
  zone_management: {
    headline: 'Treat each zone like its own lawn.',
    body: 'Front yard gets sun, backyard is shaded, side strip is sand — so they need different plans. Track them separately.',
    bullets: [
      'Unlimited zones per property',
      'Zone-specific GDD & rules',
      'Per-zone application history',
    ],
    cta: 'Set Up My Zones',
  },
  field_guide: {
    headline: 'See what\'s hitting your lawn this week.',
    body: 'Full-season weed emergence, pest activity, and where they overlap at the same GDD — so you fight on two fronts with one plan.',
    bullets: [
      'Spring-through-fall coverage',
      'Weed + pest overlap windows',
      'GDD-based action triggers',
    ],
    cta: 'Open the Field Guide',
  },
  pest_department: {
    headline: 'Time grub and armyworm apps to when the bugs are vulnerable.',
    body: 'Scout at the right GDD, treat in the right window — one app instead of three.',
    bullets: [
      'GDD-based pest lifecycle tracking',
      'Grub preventive & armyworm curative timing',
      'Product calculator + application log',
    ],
    cta: 'Get Pest Timing',
  },
  disease_department: {
    headline: 'Stop brown patch from coming back every summer.',
    body: 'Rotate FRAC groups automatically so your fungicides keep working — no resistance, no mystery outbreaks.',
    bullets: [
      'Automatic FRAC rotation tracking',
      'Disease pressure alerts by weather',
      'Preventive + curative timing',
    ],
    cta: 'Get Disease Control',
  },
  soil_analysis: {
    headline: 'Stop guessing what your lawn is missing.',
    body: 'Enter your lab numbers, get the exact amendments and rates — so you spend on what actually moves the needle.',
    bullets: [
      'Personalized recommendations from your soil test',
      'University-Extension sourced ranges',
      'Product matches with dialed-in rates',
    ],
    cta: 'Analyze My Soil',
  },
  spray_forecast: {
    headline: 'Pick the right morning to spray.',
    body: '5-day wind and weather forecast, tuned for applicators — so your product lands on the lawn, not your neighbor\'s driveway.',
    bullets: [
      'Hourly wind + best-spray windows',
      'Drift & evaporation warnings',
      'Rain-fastness countdown',
    ],
    cta: 'See My Spray Window',
  },
  unlimited_zones: {
    headline: 'Manage every corner of your property.',
    body: 'Side strip, back hill, shade garden — each gets its own plan.',
    bullets: ['Unlimited zones per property', 'Per-zone GDD', 'Per-zone history'],
    cta: 'Unlock Unlimited Zones',
  },
  priority_support: {
    headline: 'Get answers in hours, not days.',
    body: 'Priority ticket queue and direct access to the team that built TurfPulse.',
    bullets: ['Priority response', 'Direct team access', 'Feature-request fast track'],
    cta: 'Get Priority Support',
  },
};

const DEFAULT_OUTCOME = {
  headline: 'Give your lawn the full-season edge.',
  body: 'Unlock the feature set TurfPulse enthusiasts use to dial in every application, every week.',
  bullets: ['Full GDD-driven planning', 'Application logs & reminders', 'Priority support'],
  cta: 'Start My Upgrade',
};

export const UpgradeGate = ({ feature, featureLabel, children }) => {
  const { user } = useAuth();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const hasAccess = user?.allowed_features?.includes(feature);

  if (hasAccess) {
    return children;
  }

  const requiredTier = FEATURE_REQUIRED_TIER[feature] || 'homeowner_seasonal';
  const tierInfo = TIER_INFO[requiredTier];
  const outcome = FEATURE_OUTCOMES[feature] || DEFAULT_OUTCOME;

  const handleUpgrade = async (packageId) => {
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { package_id: packageId },
        { withCredentials: true, headers: { origin } }
      );
      window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
    }
  };

  return (
    <>
      <div className="relative" data-testid={`upgrade-gate-${feature}`}>
        {/* Blurred preview - capped height so overlay stays visible */}
        <div className="pointer-events-none select-none filter blur-[6px] opacity-40 max-h-[70vh] overflow-hidden">
          {children}
        </div>

        {/* Outcome-framed overlay */}
        <div className="absolute inset-x-0 top-0 z-10 flex justify-center" style={{ paddingTop: '8vh' }}>
          <Card
            className="max-w-lg w-full mx-4 shadow-2xl border-2 border-primary/20 bg-background/95 backdrop-blur-sm"
            data-testid={`upgrade-gate-card-${feature}`}
          >
            <CardHeader className="pb-3 text-left">
              <div className="text-[11px] font-semibold tracking-wider uppercase text-primary mb-2">
                {featureLabel || 'Next-level feature'}
              </div>
              <CardTitle
                className="text-2xl sm:text-3xl font-bold leading-tight text-foreground"
                data-testid={`upgrade-gate-headline-${feature}`}
              >
                {outcome.headline}
              </CardTitle>
              <p className="text-base text-muted-foreground mt-3 leading-relaxed">
                {outcome.body}
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <ul className="space-y-2.5">
                {outcome.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle weight="fill" size={18} className="text-primary shrink-0 mt-[1px]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`upgrade-gate-cta-${feature}`}
                onClick={() => setShowUpgradeDialog(true)}
                className="w-full bg-primary hover:bg-primary/90"
                size="lg"
              >
                {outcome.cta}
                <ArrowRight weight="bold" size={16} className="ml-2" />
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Included with <span className="font-semibold text-foreground">{tierInfo.name}</span> · Free trial · Cancel anytime
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upgrade Plan Selection Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Homeowner Seasonal */}
            <Card className={`border-2 border-green-500 relative overflow-hidden ${requiredTier === 'homeowner_seasonal' ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}>
              <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                POPULAR
              </div>
              <CardHeader>
                <CardTitle className="text-green-700 dark:text-green-400">Homeowner Seasonal</CardTitle>
                <p className="text-sm text-muted-foreground">Perfect for lawn season (6 months)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">$50<span className="text-lg font-normal text-muted-foreground">/6 mo</span></div>
                  <div className="text-sm text-green-600 font-medium">1 month FREE trial included</div>
                </div>
                <ul className="space-y-2 text-sm">
                  {TIER_INFO.homeowner_seasonal.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span className="text-green-500">&#10003;</span> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid="upgrade-dialog-seasonal-btn"
                  onClick={() => handleUpgrade('homeowner_seasonal')}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>

            {/* Enthusiast Annual */}
            <Card className={`border-2 border-blue-500 relative overflow-hidden ${requiredTier === 'enthusiast_annual' ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                BEST VALUE
              </div>
              <CardHeader>
                <CardTitle className="text-blue-700 dark:text-blue-400">Enthusiast Annual</CardTitle>
                <p className="text-sm text-muted-foreground">Full year, ALL features unlocked</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">$80<span className="text-lg font-normal text-muted-foreground">/year</span></div>
                  <div className="text-sm text-blue-600 font-medium">2 months FREE trial included</div>
                </div>
                <ul className="space-y-2 text-sm">
                  {TIER_INFO.enthusiast_annual.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span className="text-blue-500">&#10003;</span> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid="upgrade-dialog-annual-btn"
                  onClick={() => handleUpgrade('enthusiast_annual')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
          </div>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Cancel anytime during your free trial. No charge until trial ends.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
